import type {
  StoreAvailableProductsResponse,
  StoreCaDetailPeriod,
  StoreCaDetailResponse,
  StoreCamera,
  StoreCameraViewSession,
  StoreClockPayload,
  StoreClockResponse,
  StoreContactCallPayload,
  StoreContactCallResponse,
  StoreDashboardStatsResponse,
  StoreProductLotsResponse,
  StoreLiveCaResponse,
  StorePlanningWeekResponse,
  StoreProcessTaskCreatePayload,
  StoreProcessTaskResponse,
  StoreProcessTaskUpdatePayload,
  StoreStockDetailResponse,
  StoreStockResponse,
  StoreTaskCompletionPayload,
  StoreTaskCompletionResponse,
  StoreTodayActionsResponse,
} from '@gozoki/api-types'
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  createStoreProcessTask,
  deleteStoreProcessTask,
  logStoreContactCall,
  setStoreProcessTaskCompletion,
  storeClock,
  updateStoreProcessTask,
} from '../mutations/storeApp.mutations'
import {
  fetchStoreAvailableProducts,
  fetchStoreCaDetail,
  fetchStoreProductLots,
  fetchStoreCameraViewSession,
  fetchStoreDashboardStats,
  fetchStoreLiveCa,
  fetchStoreOwnCameras,
  fetchStorePlanningWeek,
  fetchStoreProcessTasks,
  fetchStoreOwnStock,
  fetchStoreStockDetail,
  fetchStoreTodayActions,
} from '../queries/storeApp.queries'

// CA temps réel : rafraîchi chaque minute (même cadence que le live CA réseau)
export const useStoreLiveCa = (): UseQueryResult<StoreLiveCaResponse> =>
  useQuery({
    queryKey: ['store-live-ca'],
    queryFn: fetchStoreLiveCa,
    refetchInterval: 60_000,
  })

// Scores + CA chart : agrégats lents, staleTime 5 min
export const useStoreDashboardStats = (): UseQueryResult<StoreDashboardStatsResponse> =>
  useQuery({
    queryKey: ['store-dashboard-stats'],
    queryFn: fetchStoreDashboardStats,
    staleTime: 5 * 60 * 1000,
  })

// Modale « CA 7 jours » : chargée à l'ouverture, une clé par onglet. Les fenêtres 7 et 30 jours
// ne bougent qu'à la clôture de la journée ; « Actuel » suit la journée, comme le CA temps réel.
// Après une erreur, nouvel essai chaque minute : le kiosk n'a pas d'opérateur pour relancer.
export const useStoreCaDetail = (
  period: StoreCaDetailPeriod,
): UseQueryResult<StoreCaDetailResponse> =>
  useQuery({
    queryKey: ['store-ca-detail', period],
    queryFn: () => fetchStoreCaDetail(period),
    staleTime: period === 'day' ? 60_000 : 5 * 60_000,
    refetchInterval: (query) =>
      period === 'day' || query.state.status === 'error' ? 60_000 : false,
  })

// Modale « Stock restant » : montée seulement modale ouverte, donc rafraîchie plus souvent que la
// carte (heures de rupture prévues et vendus du jour bougent en continu).
export const useStoreStockDetail = (): UseQueryResult<StoreStockDetailResponse> =>
  useQuery({
    queryKey: ['store-stock-detail'],
    queryFn: fetchStoreStockDetail,
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
  })

// Stock restant : trois agrégats sur `unique_articles`, table chaude et sans index sur
// (store_reference, state). Monté en permanence sur chaque écran boutique du réseau, donc cadencé
// à 5 min comme la timeline supply plutôt qu'à la minute.
export const useStoreOwnStock = (): UseQueryResult<StoreStockResponse> =>
  useQuery({
    queryKey: ['store-stock'],
    queryFn: fetchStoreOwnStock,
    refetchInterval: 5 * 60_000,
    staleTime: 5 * 60_000,
  })

// Produits en rayon : suit le stock, donc même cadence que la carte STOCK RESTANT.
export const useStoreAvailableProducts = (
  categoryId?: number,
): UseQueryResult<StoreAvailableProductsResponse> =>
  useQuery({
    queryKey: ['store-available-products', categoryId ?? null],
    queryFn: () => fetchStoreAvailableProducts(categoryId),
    refetchInterval: 60_000,
    staleTime: 60_000,
  })

// Détail par lot d'un produit : chargé seulement quand une fiche produit est ouverte.
export const useStoreProductLots = (
  reference: string | null,
): UseQueryResult<StoreProductLotsResponse> =>
  useQuery({
    queryKey: ['store-product-lots', reference],
    queryFn: () => fetchStoreProductLots(reference ?? ''),
    enabled: reference !== null,
    staleTime: 60_000,
  })

// Caméras de la boutique (liste stable)
export const useStoreCameras = (): UseQueryResult<StoreCamera[]> =>
  useQuery({
    queryKey: ['store-cameras'],
    queryFn: fetchStoreOwnCameras,
    staleTime: 5 * 60 * 1000,
  })

// Session viewer go2rtc : token TTL ~10 min → refetch régulier avant expiration
export const useStoreCameraViewSession = (
  enabled: boolean,
): UseQueryResult<StoreCameraViewSession> =>
  useQuery({
    queryKey: ['store-camera-view-session'],
    queryFn: fetchStoreCameraViewSession,
    enabled,
    refetchInterval: 8 * 60 * 1000,
    staleTime: 8 * 60 * 1000,
    retry: false,
  })

// Planning de la semaine en cours de la boutique : pointages qui bougent en journée, donc on
// rafraîchit régulièrement sans pour autant recharger à chaque focus.
export const useStorePlanningWeek = (
  weekStart?: string,
): UseQueryResult<StorePlanningWeekResponse> =>
  useQuery({
    queryKey: ['store-planning-week', weekStart ?? null],
    queryFn: () => fetchStorePlanningWeek(weekStart),
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  })

// Barre d'actions du bas : le dernier pointage bouge en continu pendant la journée.
export const useStoreTodayActions = (): UseQueryResult<StoreTodayActionsResponse> =>
  useQuery({
    queryKey: ['store-today-actions'],
    queryFn: fetchStoreTodayActions,
    refetchInterval: 60_000,
  })

// Appel de contact : action ponctuelle, pas de cache à invalider (l'écran ne lit pas ses logs).
export const useStoreContactCallLog = (): UseMutationResult<
  StoreContactCallResponse,
  Error,
  StoreContactCallPayload
> => useMutation({ mutationFn: logStoreContactCall })

// Pointage kiosk : après succès, rafraîchir le dernier pointage de la barre d'actions ET le planning
// de la semaine — ce dernier porte l'état de pointage par créneau (grille du dashboard + gardes
// Entrée/Sortie de la modale). Sans ça, la tablette reste figée jusqu'au refetch périodique : après
// avoir pointé son entrée, un collaborateur ne pourrait pas pointer sa sortie (créneau vu comme
// non pointé).
export const useStoreClock = (): UseMutationResult<
  StoreClockResponse,
  Error,
  StoreClockPayload
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: storeClock,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-today-actions'] })
      queryClient.invalidateQueries({ queryKey: ['store-planning-week'] })
    },
  })
}

// store-app : la boutique coche/décoche une tâche « process ». Rafraîchit le planning (qui porte
// l'état des tâches des deux widgets Process et Planning).
export const useSetStoreProcessTaskCompletion = (): UseMutationResult<
  StoreTaskCompletionResponse,
  Error,
  StoreTaskCompletionPayload & { id: number }
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: setStoreProcessTaskCompletion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-planning-week'] })
    },
  })
}

// back-office : tâches « process » déjà planifiées d'une boutique (onglet « Gérer »).
export const useStoreProcessTasks = (
  storeId: number | null,
): UseQueryResult<StoreProcessTaskResponse[]> =>
  useQuery({
    queryKey: ['store-process-tasks', storeId],
    queryFn: () => fetchStoreProcessTasks(storeId ?? 0),
    enabled: storeId !== null,
  })

// back-office : création d'une tâche « process » planifiée pour une boutique. Invalide la liste de
// la boutique ciblée pour que l'onglet « Gérer » se rafraîchisse.
export const useCreateStoreProcessTask = (): UseMutationResult<
  StoreProcessTaskResponse,
  Error,
  StoreProcessTaskCreatePayload
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createStoreProcessTask,
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: ['store-process-tasks', task.storeId] })
    },
  })
}

// back-office : édition d'une tâche « process ».
export const useUpdateStoreProcessTask = (): UseMutationResult<
  StoreProcessTaskResponse,
  Error,
  { id: number; data: StoreProcessTaskUpdatePayload }
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateStoreProcessTask,
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: ['store-process-tasks', task.storeId] })
    },
  })
}

// back-office : suppression d'une tâche « process ». La liste ciblée est invalidée par l'appelant
// (le storeId n'est pas dans la réponse) via `storeId` connu de l'écran.
export const useDeleteStoreProcessTask = (): UseMutationResult<{ success: true }, Error, number> =>
  useMutation({ mutationFn: deleteStoreProcessTask })
